import { useEffect, useRef, useState } from 'react'

type useInfiniteScrollProps = {
  container?: any
  fetchNextPage: () => void
  hasNextPage: boolean
  isLoading: boolean
}

const useInfiniteScroll = ({
  container = window,
  fetchNextPage,
  hasNextPage,
  isLoading
}: useInfiniteScrollProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const [hasScrolled, setHasScrolled] = useState(false)
  const isFetchingRef = useRef(false)

  useEffect(() => {
    if (!hasNextPage || !hasScrolled) return

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isFetchingRef.current) {
            isFetchingRef.current = true
            fetchNextPage()
          }
        },
        { threshold: 0.5 }
      )
    }

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [hasNextPage, hasScrolled, fetchNextPage])

  useEffect(() => {
    if (!isLoading) {
      isFetchingRef.current = false
    }
  }, [isLoading])

  useEffect(() => {
    const onScroll = () => setHasScrolled(true)
    container.addEventListener('scroll', onScroll)
    return () => container.removeEventListener('scroll', onScroll)
  }, [container])

  return loadMoreRef
}

export default useInfiniteScroll
